/**
 * React Query hooks for his vendor list (XC-INV2PO DB6 note §3): the Business Profile's "How you
 * order" card, the Inbox's "who supplies it?" cards and the sidebar's Inbox count all read ONE key,
 * so the three never disagree. A save or an Undo re-reads the list (it is the list he just changed),
 * marks the profile stale, and marks the Inbox's order questions stale (his plan re-reads his list at
 * its next check).
 *
 * The save id belongs to one change while it is being sent (`useChangeId`): the same change sent
 * again (after a network error or a timeout) sends the same id, so the server replays it rather than
 * filing it twice; once filed, the same change made again (after his Undo) is a new save.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useChangeId } from '@/hooks/data/useChangeId';
import { businessProfileKeys } from '@/hooks/data/useBusinessProfile';
import { orderQuestionKeys } from '@/hooks/data/useOrderQuestionsData';
import type { FactWriteResult } from '@/services/businessProfileWrites';
import { fetchVendorList, saveVendorList, undoVendorList } from '@/services/businessProfileVendors';
import type { VendorList } from '@/types/businessProfile/vendorList';

export const vendorListKeys = {
  all: ['business-profile', 'vendor-list'] as const,
};

export function useVendorList() {
  return useQuery<VendorList>({
    queryKey: vendorListKeys.all,
    queryFn: fetchVendorList,
    staleTime: 30_000,
  });
}

export interface SaveVars {
  list: VendorList;
  /** key -> the vendor he chose for that material. */
  changes: Record<string, string>;
}

function useRefresh() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: vendorListKeys.all });
    void qc.invalidateQueries({ queryKey: businessProfileKeys.all, refetchType: 'none' });
    void qc.invalidateQueries({ queryKey: orderQuestionKeys.all, refetchType: 'none' });
  };
}

export function useSaveVendorList() {
  const refresh = useRefresh();
  const { idFor, clear } = useChangeId();
  return useMutation<FactWriteResult | undefined, Error, SaveVars>({
    mutationFn: ({ list, changes }) =>
      saveVendorList(list, changes, idFor(JSON.stringify([list.based_on, changes]))),
    onSuccess: () => {
      clear();
      refresh();
    },
  });
}

export function useUndoVendorList() {
  const refresh = useRefresh();
  const { idFor, clear } = useChangeId();
  return useMutation<FactWriteResult, Error, VendorList>({
    mutationFn: (list) => undoVendorList(list, idFor(`undo ${list.undo?.undoes ?? ''}`)),
    onSuccess: () => {
      clear();
      refresh();
    },
  });
}
